import React, { useState } from "react";
import {
  FilePdfOutlined,
  EyeOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import Pagination from "antd/es/pagination";
import { Userprofile } from "../../../Entities/user-profile.entity";
import { openNotification } from "../../../Components/Notification/Index";
import { ResumeViewerDrawer } from "../../../Components/ResumeViewerDrawer";
import { formatDate } from "../../../Utils/generic-functions";

interface ResumeListProps {
  resumes: Userprofile[];
}

const ResumeList: React.FC<ResumeListProps> = ({ resumes }) => {
  const [viewerOpen, setViewerOpen] = useState(false);
  const [selectedProfileId, setSelectedProfileId] = useState<
    string | undefined
  >();
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 10;

  const handleViewResume = (resume: Userprofile) => {
    setSelectedProfileId(resume._id);
    setViewerOpen(true);
  };

  const handleCloseViewer = () => {
    setViewerOpen(false);
    setSelectedProfileId(undefined);
  };

  const handleDownloadResume = async (resume: Userprofile) => {
    try {
      const url = `https://hiring-assistant-files.s3.ap-south-1.amazonaws.com/profile/${resume.userId}/${resume.filename}`;

      // Fetch the file as a blob to handle CORS properly
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error("Failed to download file");
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      // Create a temporary anchor element to trigger download
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = resume.filename || "resume.pdf";
      document.body.appendChild(link);
      link.click();

      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Error downloading resume:", error);
      openNotification(
        "error",
        "Download Failed",
        "Failed to download the resume. Please try again.",
      );
    }
  };

  const sortedResumes = [...resumes].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );

  const paginatedResumes = sortedResumes.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  );

  return (
    <div className="resumes">
      {paginatedResumes.map((resume) => (
        <div key={resume._id} className="resumes__item">
          <div className="resumes__left">
            <div className="resumes__icon">
              <FilePdfOutlined />
            </div>
            <div className="resumes__info">
              <h3 className="resumes__name">{resume.filename}</h3>
              <div className="resumes__meta">
                <span>Uploaded {formatDate(resume.createdAt)}</span>
              </div>
            </div>
          </div>
          <div className="resumes__actions">
            <button
              className="resumes__btn"
              onClick={() => handleViewResume(resume)}
            >
              <EyeOutlined /> View
            </button>
            <button
              className="resumes__btn"
              onClick={() => handleDownloadResume(resume)}
            >
              <DownloadOutlined /> Download
            </button>
          </div>
        </div>
      ))}

      {sortedResumes.length > PAGE_SIZE && (
        <Pagination
          current={currentPage}
          pageSize={PAGE_SIZE}
          total={sortedResumes.length}
          onChange={setCurrentPage}
          showSizeChanger={false}
          style={{ marginTop: "1rem", display: "flex", justifyContent: "flex-end" }}
        />
      )}

      <ResumeViewerDrawer
        open={viewerOpen}
        onClose={handleCloseViewer}
        profileId={selectedProfileId}
      />
    </div>
  );
};

export default ResumeList;
